// hooks/usePendingMembershipRequests.js
//
// The Org Admin nav badge: how many portal-user membership requests are
// waiting for an answer (owner 2026-10-07 — "a small red number of requests
// in the nav menu"). Same red pill and the same rules as the App Access
// badge in usePendingApprovals.js:
//
//   • refreshed on org/auth change and on EVERY route navigation
//   • NOT on a timer — no polling
//   • non-admins (and no org) read 0 without a request
//   • every fetch error is swallowed silently: the badge keeps its last
//     value; a broken count must never toast at someone browsing the portal
//
// One addition: the Org Admin page answers requests without changing the
// route, so it announces its fresh list through
// announceMembershipRequestCount() and the badge drops straight away.
'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { normalizeKeys } from '@/utils/apiHelpers';
import { countOpenMembershipRequests } from '@/utils/membershipStatus';
import { logger } from '@/utils/log';

const COUNT_EVENT = 'fs:membership-request-count';

/** The Org Admin page calls this after every load of its request list. */
export function announceMembershipRequestCount(orgId, count) {
    if (typeof window === 'undefined' || !orgId) return;
    window.dispatchEvent(new CustomEvent(COUNT_EVENT, { detail: { orgId, count } }));
}

export default function usePendingMembershipRequests() {
    const pathname = usePathname();
    const { authKey, currentOrg } = useAuth() || {};

    const orgId = currentOrg?.orgId || currentOrg?.OrgId || '';
    const isAdmin = !!currentOrg?.isAdmin;

    const [count, setCount] = useState(0);

    // Stale responses must not clobber a newer org's count — tag each fetch.
    const fetchSeq = useRef(0);

    useEffect(() => {
        if (!authKey || !orgId || !isAdmin) {
            setCount(0);
            return;
        }

        const seq = ++fetchSeq.current;
        (async () => {
            try {
                // /api/fsauth (root), same proxy the Org Admin page uses.
                const res = await fetch('/api/fsauth/lacsso/getorgmembershiprequests', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ Key: authKey, OrgId: orgId }),
                });
                if (!res.ok) return;                  // swallowed — keep last value
                const data = normalizeKeys(await res.json());
                if (!data?.success) return;
                if (fetchSeq.current !== seq) return; // a newer refresh superseded this
                setCount(countOpenMembershipRequests(data.requests));
            } catch (err) {
                logger.warn('[membershipRequestsBadge]', err);
            }
        })();
    }, [authKey, orgId, isAdmin, pathname]);

    // Answers given on the Org Admin page update the badge without a reload.
    useEffect(() => {
        if (!orgId || !isAdmin) return undefined;
        const onCount = (e) => {
            if (e.detail?.orgId !== orgId) return;
            fetchSeq.current++;                       // outrank any fetch in flight
            setCount(Number(e.detail.count) || 0);
        };
        window.addEventListener(COUNT_EVENT, onCount);
        return () => window.removeEventListener(COUNT_EVENT, onCount);
    }, [orgId, isAdmin]);

    return count;
}
